import { FeedbackAgentContextSchema, type FeedbackAgentContext } from './types.js';

export const SEND_FEEDBACK_COMMAND_HASH = '#feedback';
export const REPORT_BUG_COMMAND_HASH = '#report-bug';

export type FeedbackCommandChannel = 'feedback' | 'bug';

export type FeedbackCommand = {
  hash: typeof SEND_FEEDBACK_COMMAND_HASH | typeof REPORT_BUG_COMMAND_HASH;
  /** User-menu / ⌘K hash toggles; a session action always opens. */
  mode: 'open' | 'toggle';
  channel: FeedbackCommandChannel;
  agentContext?: FeedbackAgentContext;
};

/**
 * Absolute session URL for feedback post details. `agentPath` is the
 * org-scoped path from `useOrgRoutes().agent(id)`.
 */
export function buildSessionFeedbackAgentContext(input: {
  agentId: string;
  agentName?: string;
  agentStatus?: string;
  agentPath: string;
}): FeedbackAgentContext {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const agentUrl =
    origin && input.agentPath.startsWith('/') ? `${origin}${input.agentPath}` : input.agentPath;
  return {
    agentId: input.agentId,
    agentName: input.agentName,
    agentStatus: input.agentStatus,
    agentUrl,
  };
}

function channelForHash(
  hash: unknown,
): { hash: FeedbackCommand['hash']; channel: FeedbackCommandChannel } | null {
  if (hash === SEND_FEEDBACK_COMMAND_HASH) {
    return { hash: SEND_FEEDBACK_COMMAND_HASH, channel: 'feedback' };
  }
  if (hash === REPORT_BUG_COMMAND_HASH) {
    return { hash: REPORT_BUG_COMMAND_HASH, channel: 'bug' };
  }
  return null;
}

/**
 * Parse a `plugin:command` detail. The user menu still sends the hash string;
 * the session action sends `{ hash, agentContext }`.
 */
export function parseFeedbackCommand(detail: unknown): FeedbackCommand | null {
  if (typeof detail === 'string') {
    const parsed = channelForHash(detail);
    if (!parsed) return null;
    return { hash: parsed.hash, mode: 'toggle', channel: parsed.channel };
  }
  if (!detail || typeof detail !== 'object' || !('hash' in detail)) return null;
  const parsed = channelForHash(detail.hash);
  if (!parsed) return null;

  const parsedContext = FeedbackAgentContextSchema.safeParse(
    'agentContext' in detail ? detail.agentContext : undefined,
  );
  return {
    hash: parsed.hash,
    mode: 'open',
    channel: parsed.channel,
    ...(parsedContext.success ? { agentContext: parsedContext.data } : {}),
  };
}
